import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Coins,
  Flag,
  Loader2,
  LogOut,
  PartyPopper,
  Plus,
  Rocket,
  Search,
  ShieldAlert,
  TrendingUp,
  Users,
  Video,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import {
  getAdminDailyMetrics,
  getAdminGenerationStats,
  getAdminOverview,
} from "@/lib/admin-metrics.functions";
import {
  forceSignOutUser,
  getRecentAdminActions,
  getSystemHealth,
  grantTicketsToUser,
  searchUsers,
  setUserBanned,
} from "@/lib/admin-users.functions";
import { getLaunchedAt } from "@/lib/deploy-checklist.functions";
import { getVoiceErrorStats } from "@/lib/voice-error-stats.functions";
import {
  VOICE_PHASE_LABELS,
  VOICE_PHASE_ORDER,
  type VoicePhaseKey as VoicePhaseKeyLocal,
} from "@/lib/voiceChange/phases";

type Range = "today" | "week" | "month";
type View = "overview" | "users" | "health" | "audit";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "管理コンソール | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminIndex,
});

function AdminIndex() {
  const isAdminFn = useServerFn(getIsAdmin);
  const launchedAtFn = useServerFn(getLaunchedAt);
  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState<View>("overview");
  const [launchedAt, setLaunchedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          setIsAdmin(false);
          return;
        }
        const headers = { Authorization: `Bearer ${sessionData.session.access_token}` };
        const res = await isAdminFn({ headers });
        setIsAdmin(res.isAdmin);
        if (res.isAdmin) {
          try {
            const launch = await launchedAtFn({ headers });
            setLaunchedAt(launch.launched_at);
          } catch (e) {
            console.error("launched_at fetch failed", e);
          }
        }
      } catch (e) {
        console.error("admin check failed", e);
        setIsAdmin(false);
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn, launchedAtFn]);

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">アクセス権がありません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この画面は管理者ロールを持つユーザーのみが利用できます。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">管理画面</h1>
            <p className="mt-2 text-muted-foreground">運営オペレーション用ツール</p>
            {launchedAt && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <PartyPopper className="h-3.5 w-3.5" />
                ローンチ済み: {new Date(launchedAt).toLocaleDateString("ja-JP", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/qa-checklist">
                <CheckCircle2 className="mr-2 h-4 w-4" />
                QAチェックリスト
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/deploy-checklist">
                <Rocket className="mr-2 h-4 w-4" />
                デプロイチェックリスト
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/reports">
                <Flag className="mr-2 h-4 w-4" />
                通報管理へ
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/contacts">
                <Flag className="mr-2 h-4 w-4" />
                お問い合わせ
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/moderation">
                <ShieldAlert className="mr-2 h-4 w-4" />
                モデレーション監視
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/prompts">
                <Activity className="mr-2 h-4 w-4" />
                プロンプト履歴
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/lab-runs">
                <Activity className="mr-2 h-4 w-4" />
                Lab コスト履歴
              </Link>
            </Button>
            <Button asChild variant="default">
              <Link to="/admin/lab">
                <Activity className="mr-2 h-4 w-4" />
                AI Lab
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/voice-lab">
                <Activity className="mr-2 h-4 w-4" />
                音声変換ラボ
              </Link>
            </Button>
          </div>
        </div>

        <Tabs value={view} onValueChange={(v) => setView(v as View)} className="mt-6">
          <TabsList>
            <TabsTrigger value="overview">概要</TabsTrigger>
            <TabsTrigger value="users">ユーザー管理</TabsTrigger>
            <TabsTrigger value="health">システム健全性</TabsTrigger>
            <TabsTrigger value="audit">操作ログ</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-6">
          {view === "overview" && <OverviewSection />}
          {view === "users" && <UsersSection />}
          {view === "health" && (
            <div className="space-y-6">
              <HealthSection />
              <VoiceHealthSection />
            </div>
          )}
          {view === "audit" && <AuditSection />}
        </div>
      </div>
    </main>
  );
}

/* ============================================================
 * Overview
 * ============================================================ */
function OverviewSection() {
  const overviewFn = useServerFn(getAdminOverview);
  const dailyFn = useServerFn(getAdminDailyMetrics);
  const statsFn = useServerFn(getAdminGenerationStats);
  const [range, setRange] = useState<Range>("month");
  const [overview, setOverview] = useState<Awaited<
    ReturnType<typeof getAdminOverview>
  > | null>(null);
  const [daily, setDaily] = useState<Awaited<
    ReturnType<typeof getAdminDailyMetrics>
  > | null>(null);
  const [stats, setStats] = useState<Awaited<
    ReturnType<typeof getAdminGenerationStats>
  > | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const headers = sessionData.session
          ? { Authorization: `Bearer ${sessionData.session.access_token}` }
          : undefined;
        const [ov, dl, st] = await Promise.all([
          overviewFn({ data: { range }, headers }),
          dailyFn({ headers }),
          statsFn({ data: { range }, headers }),
        ]);
        if (cancelled) return;
        setOverview(ov);
        setDaily(dl);
        setStats(st);
      } catch (e) {
        console.error("metrics fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range, overviewFn, dailyFn, statsFn]);

  return (
    <div>
      <div className="flex justify-end">
        <Tabs value={range} onValueChange={(v) => setRange(v as Range)}>
          <TabsList>
            <TabsTrigger value="today">今日</TabsTrigger>
            <TabsTrigger value="week">今週</TabsTrigger>
            <TabsTrigger value="month">今月</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <section className="mt-6">
        <h2 className="mb-3 text-lg font-semibold">概要メトリクス</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            icon={<Users className="h-5 w-5" />}
            label="新規サインアップ"
            value={overview?.signups ?? "—"}
            loading={loading}
          />
          <MetricCard
            icon={<Activity className="h-5 w-5" />}
            label="DAU (24h)"
            value={overview?.dau ?? "—"}
            loading={loading}
          />
          <MetricCard
            icon={<Video className="h-5 w-5" />}
            label="生成数"
            value={overview?.generationsTotal ?? "—"}
            sub={
              overview
                ? `完了 ${overview.generationsCompleted} / 失敗 ${overview.generationsFailed}`
                : undefined
            }
            loading={loading}
          />
          <MetricCard
            icon={<Coins className="h-5 w-5" />}
            label="売上合計"
            value={
              overview ? `¥${overview.revenueTotalJpy.toLocaleString()}` : "—"
            }
            sub={
              overview
                ? `買切 ¥${overview.revenuePurchaseJpy.toLocaleString()} / サブ ¥${overview.revenueSubscriptionJpy.toLocaleString()}`
                : undefined
            }
            loading={loading}
          />
        </div>
        {overview && (
          <p className="mt-3 text-xs text-muted-foreground">
            エラー率: {(overview.errorRate * 100).toFixed(1)}%
          </p>
        )}
      </section>

      <section className="mt-10">
        <h2 className="mb-3 text-lg font-semibold">売上推移（直近30日）</h2>
        <Card className="p-4">
          <div className="h-72 w-full">
            {daily && daily.rows.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily.rows}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue_purchase_jpy"
                    name="買い切り"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue_subscription_jpy"
                    name="サブスク"
                    stroke="hsl(var(--chart-2, 220 70% 50%))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart loading={loading} />
            )}
          </div>
        </Card>
      </section>

      <section className="mt-10 grid gap-6 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
            <TrendingUp className="h-4 w-4" /> 品質ティア別生成数
          </h3>
          <div className="h-64">
            {stats && Object.keys(stats.byTier).length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={Object.entries(stats.byTier).map(([k, v]) => ({
                    tier: k,
                    count: v,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="tier" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart loading={loading} />
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-base font-semibold">処理サマリー</h3>
          <dl className="space-y-3 text-sm">
            <Stat
              label="成功率"
              value={stats ? `${(stats.successRate * 100).toFixed(1)}%` : "—"}
            />
            <Stat
              label="完了 / 失敗"
              value={stats ? `${stats.completed} / ${stats.failed}` : "—"}
            />
            <Stat
              label="平均処理時間（中央値）"
              value={stats ? `${Math.round(stats.medianDurationSec)} 秒` : "—"}
            />
            <div>
              <dt className="mb-1 text-muted-foreground">プロバイダ別</dt>
              <dd>
                {stats && Object.keys(stats.byModel).length > 0 ? (
                  <ul className="space-y-1">
                    {Object.entries(stats.byModel).map(([m, c]) => (
                      <li
                        key={m}
                        className="flex justify-between border-b border-border/30 py-1"
                      >
                        <span className="font-mono text-xs">{m}</span>
                        <span className="tabular-nums">{c}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">データなし</span>
                )}
              </dd>
            </div>
          </dl>
        </Card>
      </section>
    </div>
  );
}

/* ============================================================
 * Users
 * ============================================================ */
type UserRow = {
  user_id: string;
  display_name: string | null;
  plan: string;
  banned: boolean;
  banned_reason: string | null;
  verified: boolean;
  created_at: string;
  trial_uses_count: number;
  stripe_customer_id: string | null;
  email: string | null;
  credits_total: number;
};

function UsersSection() {
  const searchFn = useServerFn(searchUsers);
  const grantFn = useServerFn(grantTicketsToUser);
  const banFn = useServerFn(setUserBanned);
  const signOutFn = useServerFn(forceSignOutUser);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [grantTarget, setGrantTarget] = useState<UserRow | null>(null);
  const [grantAmount, setGrantAmount] = useState("10");
  const [grantReason, setGrantReason] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const [banConfirm, setBanConfirm] = useState<{
    user: UserRow;
    next: boolean;
  } | null>(null);
  const [banReason, setBanReason] = useState("");
  const [banBusy, setBanBusy] = useState(false);

  const [signOutConfirm, setSignOutConfirm] = useState<UserRow | null>(null);

  async function getHeaders() {
    const { data } = await supabase.auth.getSession();
    return data.session
      ? { Authorization: `Bearer ${data.session.access_token}` }
      : undefined;
  }

  async function runSearch() {
    setLoading(true);
    setHasSearched(true);
    try {
      const headers = await getHeaders();
      const res = await searchFn({ data: { query, limit: 30 }, headers });
      setRows(res.users as UserRow[]);
    } catch (e) {
      console.error(e);
      toast.error("検索に失敗しました");
    } finally {
      setLoading(false);
    }
  }

  async function submitGrant() {
    if (!grantTarget) return;
    const amt = parseInt(grantAmount, 10);
    if (!Number.isFinite(amt) || amt < 1) {
      toast.error("付与枚数は1以上の整数を指定してください");
      return;
    }
    if (!grantReason.trim()) {
      toast.error("付与理由を入力してください");
      return;
    }
    setGrantBusy(true);
    try {
      const headers = await getHeaders();
      await grantFn({
        data: {
          targetUserId: grantTarget.user_id,
          amount: amt,
          reason: grantReason.trim(),
        },
        headers,
      });
      toast.success(`${amt}枚のクレジットを付与しました`);
      setGrantTarget(null);
      setGrantAmount("10");
      setGrantReason("");
      await runSearch();
    } catch (e) {
      console.error(e);
      toast.error("付与に失敗しました");
    } finally {
      setGrantBusy(false);
    }
  }

  async function submitBan() {
    if (!banConfirm) return;
    setBanBusy(true);
    try {
      const headers = await getHeaders();
      await banFn({
        data: {
          targetUserId: banConfirm.user.user_id,
          banned: banConfirm.next,
          reason: banReason.trim(),
        },
        headers,
      });
      toast.success(banConfirm.next ? "アカウントを停止しました" : "停止を解除しました");
      setBanConfirm(null);
      setBanReason("");
      await runSearch();
    } catch (e) {
      console.error(e);
      toast.error("操作に失敗しました");
    } finally {
      setBanBusy(false);
    }
  }

  async function submitSignOut() {
    if (!signOutConfirm) return;
    try {
      const headers = await getHeaders();
      await signOutFn({
        data: { targetUserId: signOutConfirm.user_id, reason: "" },
        headers,
      });
      toast.success("強制サインアウトしました");
      setSignOutConfirm(null);
    } catch (e) {
      console.error(e);
      toast.error("操作に失敗しました");
    }
  }

  return (
    <div>
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="email / 表示名 / user_id"
              className="pl-9"
              maxLength={255}
            />
          </div>
          <Button onClick={runSearch} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "検索"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          空欄で検索すると最新の登録ユーザー30件を表示します。
        </p>
      </Card>

      <Card className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ユーザー</TableHead>
              <TableHead>プラン</TableHead>
              <TableHead>残クレジット</TableHead>
              <TableHead>登録日</TableHead>
              <TableHead>状態</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasSearched ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  検索ボタンを押すと結果が表示されます
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  該当するユーザーが見つかりませんでした
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell>
                    <div className="font-medium">{u.display_name ?? "(no name)"}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {u.email ?? "—"}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {u.user_id}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{u.plan}</Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{u.credits_total}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString("ja-JP")}
                  </TableCell>
                  <TableCell>
                    {u.banned ? (
                      <Badge variant="destructive">停止中</Badge>
                    ) : (
                      <Badge variant="secondary">有効</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setGrantTarget(u)}
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        付与
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSignOutConfirm(u)}
                        title="強制ログアウト"
                      >
                        <LogOut className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={u.banned ? "secondary" : "destructive"}
                        onClick={() => {
                          setBanReason(u.banned_reason ?? "");
                          setBanConfirm({ user: u, next: !u.banned });
                        }}
                      >
                        <Ban className="mr-1 h-3 w-3" />
                        {u.banned ? "解除" : "停止"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* クレジット付与モーダル */}
      <Dialog open={!!grantTarget} onOpenChange={(o) => !o && setGrantTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>クレジットを付与</DialogTitle>
            <DialogDescription>
              {grantTarget?.display_name ?? grantTarget?.email ?? grantTarget?.user_id} に
              クレジット（buyout 残高）を加算します。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">付与枚数</label>
              <Input
                type="number"
                min={1}
                max={1000}
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">理由（必須）</label>
              <Textarea
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="例: 障害のお詫び対応"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantTarget(null)}>
              キャンセル
            </Button>
            <Button onClick={submitGrant} disabled={grantBusy}>
              {grantBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              付与する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban 確認 */}
      <AlertDialog
        open={!!banConfirm}
        onOpenChange={(o) => !o && setBanConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {banConfirm?.next ? "アカウントを停止しますか？" : "停止を解除しますか？"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {banConfirm?.next
                ? "停止中のユーザーはログインできなくなり、進行中のセッションも無効化されます。"
                : "停止状態を解除し、再度ログイン可能にします。"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            placeholder={banConfirm?.next ? "停止理由（任意）" : "解除理由（任意）"}
            maxLength={500}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={submitBan} disabled={banBusy}>
              {banBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {banConfirm?.next ? "停止する" : "解除する"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 強制サインアウト確認 */}
      <AlertDialog
        open={!!signOutConfirm}
        onOpenChange={(o) => !o && setSignOutConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>強制サインアウトしますか？</AlertDialogTitle>
            <AlertDialogDescription>
              対象ユーザーの全セッションを無効化します。次回操作時に再ログインが必要になります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={submitSignOut}>実行</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ============================================================
 * Health
 * ============================================================ */
function HealthSection() {
  const healthFn = useServerFn(getSystemHealth);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSystemHealth>
  > | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const headers = sessionData.session
          ? { Authorization: `Bearer ${sessionData.session.access_token}` }
          : undefined;
        const res = await healthFn({ headers });
        if (!cancelled) setData(res);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [healthFn]);

  const errorBadge = useMemo(() => {
    if (!data) return null;
    const r = data.errorRate;
    if (r >= 0.2)
      return (
        <Badge variant="destructive">
          <AlertTriangle className="mr-1 h-3 w-3" />
          異常 ({(r * 100).toFixed(1)}%)
        </Badge>
      );
    if (r >= 0.05)
      return (
        <Badge variant="secondary">
          <AlertTriangle className="mr-1 h-3 w-3" />
          警戒 ({(r * 100).toFixed(1)}%)
        </Badge>
      );
    return (
      <Badge variant="secondary">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        正常 ({(r * 100).toFixed(1)}%)
      </Badge>
    );
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">直近{data.windowHours}時間の状態</h2>
          {errorBadge}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            icon={<Video className="h-5 w-5" />}
            label="生成総数"
            value={data.total}
          />
          <MetricCard
            icon={<Activity className="h-5 w-5" />}
            label="進行中"
            value={data.pending}
            sub={`完了 ${data.completed} / 失敗 ${data.failed}`}
          />
          <MetricCard
            icon={<TrendingUp className="h-5 w-5" />}
            label="平均処理時間"
            value={`${Math.round(data.avgDurationSec)} 秒`}
          />
          <MetricCard
            icon={<Coins className="h-5 w-5" />}
            label="API実コスト概算"
            value={`¥${data.estimatedCostJpy.toLocaleString()}`}
            sub="generations.cost_jpy 集計"
          />
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 text-base font-semibold">直近の失敗ジョブ（最大10件）</h3>
        {data.recentFailures.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            失敗はありません
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>発生時刻</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>エラー</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recentFailures.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(f.created_at).toLocaleString("ja-JP")}
                  </TableCell>
                  <TableCell>{f.quality_tier ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {f.model_used ?? "—"}
                  </TableCell>
                  <TableCell className="max-w-md truncate text-xs">
                    {f.error_message ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ============================================================
 * Audit log
 * ============================================================ */
function AuditSection() {
  const fn = useServerFn(getRecentAdminActions);
  const [actions, setActions] = useState<
    Awaited<ReturnType<typeof getRecentAdminActions>>["actions"]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const headers = sessionData.session
          ? { Authorization: `Bearer ${sessionData.session.access_token}` }
          : undefined;
        const res = await fn({ headers });
        if (!cancelled) setActions(res.actions);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fn]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>日時</TableHead>
            <TableHead>操作</TableHead>
            <TableHead>対象</TableHead>
            <TableHead>理由</TableHead>
            <TableHead>詳細</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {actions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                操作ログがまだありません
              </TableCell>
            </TableRow>
          ) : (
            actions.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleString("ja-JP")}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{a.action}</Badge>
                </TableCell>
                <TableCell className="font-mono text-[11px]">
                  {a.target_user_id ?? "—"}
                </TableCell>
                <TableCell className="max-w-xs truncate text-xs">
                  {a.reason ?? "—"}
                </TableCell>
                <TableCell className="font-mono text-[10px] text-muted-foreground">
                  {a.metadata ? JSON.stringify(a.metadata) : "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ============================================================
 * Shared
 * ============================================================ */
function MetricCard({
  icon,
  label,
  value,
  sub,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: string;
  loading?: boolean;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">
        {loading && value === "—" ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          value
        )}
      </div>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-border/30 pb-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function EmptyChart({ loading }: { loading?: boolean }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <span>データがありません</span>
      )}
    </div>
  );
}

/* ============================================================
 * Voice Change 健全性（#5 エラー監視）
 * ============================================================ */
function VoiceHealthSection() {
  const statsFn = useServerFn(getVoiceErrorStats);
  const [windowHours, setWindowHours] = useState(168);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getVoiceErrorStats>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const headers = sessionData.session
          ? { Authorization: `Bearer ${sessionData.session.access_token}` }
          : undefined;
        const res = await statsFn({ data: { windowHours }, headers });
        if (!cancelled) setData(res);
      } catch (e) {
        console.error("voice error stats failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statsFn, windowHours]);

  const rate = data ? data.failureRate : 0;
  const rateBadge = !data ? null : rate >= 0.2 ? (
    <Badge variant="destructive">
      <AlertTriangle className="mr-1 h-3 w-3" />
      異常 ({(rate * 100).toFixed(1)}%)
    </Badge>
  ) : rate >= 0.05 ? (
    <Badge variant="secondary">
      <AlertTriangle className="mr-1 h-3 w-3" />
      警戒 ({(rate * 100).toFixed(1)}%)
    </Badge>
  ) : (
    <Badge variant="secondary">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      正常 ({(rate * 100).toFixed(1)}%)
    </Badge>
  );

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Activity className="h-5 w-5" /> 音声変換の健全性
        </h2>
        <div className="flex items-center gap-2">
          {rateBadge}
          <Tabs
            value={String(windowHours)}
            onValueChange={(v) => setWindowHours(Number(v))}
          >
            <TabsList>
              <TabsTrigger value="24">24h</TabsTrigger>
              <TabsTrigger value="168">7日</TabsTrigger>
              <TabsTrigger value="720">30日</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={<Video className="h-5 w-5" />}
          label="総ジョブ数"
          value={data?.total ?? "—"}
          sub={
            data
              ? `完了 ${data.completed} / 進行中 ${data.inProgress} / 中断 ${data.cancelled}`
              : undefined
          }
          loading={loading}
        />
        <MetricCard
          icon={<Ban className="h-5 w-5" />}
          label="失敗数"
          value={data?.failed ?? "—"}
          loading={loading}
        />
        <MetricCard
          icon={<AlertTriangle className="h-5 w-5" />}
          label="失敗率"
          value={data ? `${(data.failureRate * 100).toFixed(1)}%` : "—"}
          sub={data ? `分母 ${data.failureRateDenominator} 件` : undefined}
          loading={loading}
        />
        <MetricCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="中央処理時間"
          value={data ? `${Math.round(data.medianDurationSec)} 秒` : "—"}
          sub="完了ジョブのみ"
          loading={loading}
        />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        ※失敗率 = failed / (completed + failed)（進行中・中断は分母から除外）
      </p>

      <div className="mt-6 space-y-8">
        <div>
          <h3 className="mb-2 text-base font-semibold">フェーズ別の失敗内訳</h3>
          <ul className="space-y-1 text-sm">
            {VOICE_PHASE_ORDER.map((k) => (
              <li
                key={k}
                className="flex items-center justify-between border-b border-border/30 py-1.5"
              >
                <span className={k === "unexpected" ? "text-destructive" : undefined}>
                  {VOICE_PHASE_LABELS[k]}
                </span>
                <span className="tabular-nums font-semibold">
                  {data ? data.byPhase[k] : "—"}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between py-1.5 text-muted-foreground">
              <span>中断 (cancelled・別集計)</span>
              <span className="tabular-nums">{data ? data.cancelled : "—"}</span>
            </li>
          </ul>
          {data && data.unexpectedCount > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                progress=100 かつ failed のジョブが {data.unexpectedCount} 件あります。
                状態遷移の想定外パスなのでエラー内容を確認してください。
              </span>
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-base font-semibold">直近の失敗（最大20件）</h3>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !data || data.recentFailures.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              失敗はありません
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>発生時刻(JST)</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>尺</TableHead>
                    <TableHead>進捗</TableHead>
                    <TableHead className="whitespace-nowrap">フェーズ</TableHead>
                    <TableHead>エラー</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recentFailures.map((f) => (
                    <TableRow
                      key={f.id}
                      className="cursor-pointer"
                      onClick={() => setOpenRow(openRow === f.id ? null : f.id)}
                    >
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(f.createdAt).toLocaleString("ja-JP", {
                          timeZone: "Asia/Tokyo",
                        })}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{f.userIdShort}</TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {f.durationSeconds ? `${Math.round(Number(f.durationSeconds))}s` : "—"}
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">{f.progress}%</TableCell>
                      <TableCell className="text-xs">
                        {VOICE_PHASE_LABELS[f.phase as VoicePhaseKeyLocal].split(" ")[0]}
                      </TableCell>
                      <TableCell
                        className={`max-w-xl text-xs ${openRow === f.id ? "whitespace-pre-wrap break-words" : "truncate"}`}
                      >
                        {f.error ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
